import { IconX } from "@tabler/icons-react";
import { useState } from "react";
import PrimaryButton from "../../../components/PrimaryButton";
import InputField from "../../../components/common/InputField";

type Props = {
  onClose: () => void;
  refetch:()=>void;
  setFilters:any
};

export default function FilterSidebar({ onClose, setFilters,refetch }: Props) {
  const [activeTab, setActiveTab] = useState("All");

  // ✅ All filter fields in a single state
  const [rawFilters, setRawFilters] = useState({
    awbNumber: "",
    seller: "",
    warehouseName: "",
    paymentType: "",
    codAmount: "",
    sameDate: "",
    entryDate: "",
    closeDate: "",
    senderCityName: "",
    receiverCityName: "",
    city: "",
    serviceType: "",
    platform: "",
    shippingCompany: "",
    shippingAwbNumber: "",
  });

  const tabs = ["All", "Details", "Date", "Location", "Order"];

  // ✅ Handle input changes dynamically
  const handleChange = (key: string, value: string) => {
    setRawFilters((prev:any) => ({ ...prev, [key]: value }));
  };

  // ✅ On apply rawFilters — format the data as array
  const handleApplyFilters = () => {
    const formattedFilters = Object.entries(rawFilters)
      .filter(([_, value]) => value.trim() !== "")
      .map(([key, value]) => ({ [key]: value }));

      setFilters(formattedFilters)
      refetch();

    // console.log("Final Filters:", formattedFilters);

    // You can send formattedFilters to API or parent component here
    onClose();
  };

  return (
    <div className="relative border rounded-lg border-gray-200 w-full flex flex-col justify-start overflow-hidden bg-transparent my-2">
      {/* Header */}
      <div>
        <div className="flex justify-between items-center px-6 py-8 border-b border-gray-200 bg-transparent sticky top-0 z-10 shrink-0">
          <h2 className="text-base font-semibold text-[#212121]">Filter Settings</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={handleApplyFilters}
              className="px-3.5 py-1.5 bg-[#FBD037] hover:bg-[#e2bd30] text-[#131327] font-bold rounded-lg text-xs transition-all shadow-sm active:scale-95 cursor-pointer"
            >
              Apply Filters
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-700 cursor-pointer"
              title="Close"
            >
              <IconX size={18} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 px-4 py-3 overflow-x-auto sticky top-12 z-50 bg-transparent shrink-0">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`text-xs w-full font-medium px-3.5 py-1.5 rounded-full border transition-all ${
                activeTab === tab
                  ? "bg-black text-white border-black"
                  : "text-[#1E1E1E] border-[#DADADA] bg-slate-100 hover:bg-slate-200/60"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Filter Form */}
        <div className="px-4 space-y-5 pb-6">
          {(activeTab === "All" || activeTab === "Details") && (
            <div className="space-y-3">
              <h2 className="text-[#212121]/80 uppercase text-xs font-bold tracking-wider">DETAILS</h2>
              <div className="space-y-3">
                <InputField
                  label="AWB No. (Optional)"
                  placeholder="Enter AWB, Reference Number, or Mobile Number (comma-separated)"
                  value={rawFilters.awbNumber}
                  onChange={(e) => handleChange("awbNumber", e.target.value)}
                  inputClassName="bg-yellow-50"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <InputField
                    label="Seller"
                    placeholder="Amazon"
                    value={rawFilters.seller}
                    onChange={(e) => handleChange("seller", e.target.value)}
                    inputClassName="bg-yellow-50"
                  />
                  <InputField
                    label="Warehouse"
                    placeholder="Jaipur"
                    value={rawFilters.warehouseName}
                    onChange={(e) => handleChange("warehouseName", e.target.value)}
                    inputClassName="bg-yellow-50"
                  />
                  <InputField
                    label="Payment Mode"
                    placeholder="COD/ CC"
                    value={rawFilters.paymentType}
                    onChange={(e) => handleChange("paymentType", e.target.value)}
                    inputClassName="bg-yellow-50"
                  />
                  <InputField
                    label="COD Amount"
                    placeholder="0.5 SAR"
                    value={rawFilters.codAmount}
                    onChange={(e) => handleChange("codAmount", e.target.value)}
                    inputClassName="bg-yellow-50"
                  />
                </div>
              </div>
            </div>
          )}

          {(activeTab === "All" || activeTab === "Date") && (
            <div className="space-y-3">
              <h2 className="text-[#212121]/80 uppercase text-xs font-bold tracking-wider">DATE SETTINGS</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <InputField
                  type="date"
                  label="Same Date"
                  placeholder={""}
                  value={rawFilters.sameDate}
                  onChange={(e) => handleChange("sameDate", e.target.value)}
                  inputClassName="bg-yellow-50"
                />
                <InputField
                  type="date"
                  label="Entry Date"
                  placeholder={""}
                  value={rawFilters.entryDate}
                  onChange={(e) => handleChange("entryDate", e.target.value)}
                  inputClassName="bg-yellow-50"
                />
                <InputField
                  type="date"
                  label="Close Date"
                  placeholder={""}
                  value={rawFilters.closeDate}
                  onChange={(e) => handleChange("closeDate", e.target.value)}
                  inputClassName="bg-yellow-50"
                />
              </div>
            </div>
          )}

          {(activeTab === "All" || activeTab === "Location") && (
            <div className="space-y-3">
              <h2 className="text-[#212121]/80 uppercase text-xs font-bold tracking-wider">LOCATION SETTINGS</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <InputField
                  label="Origin"
                  placeholder="Riyadh"
                  value={rawFilters.senderCityName}
                  onChange={(e) => handleChange("senderCityName", e.target.value)}
                  inputClassName="bg-yellow-50"
                />
                <InputField
                  label="Destination"
                  placeholder="Jeddah"
                  value={rawFilters.receiverCityName}
                  onChange={(e) => handleChange("receiverCityName", e.target.value)}
                  inputClassName="bg-yellow-50"
                />
                <InputField
                  label="City"
                  placeholder="Mecca"
                  value={rawFilters.city}
                  onChange={(e) => handleChange("city", e.target.value)}
                  inputClassName="bg-yellow-50"
                />
              </div>
            </div>
          )}

          {(activeTab === "All" || activeTab === "Order") && (
            <div className="space-y-3">
              <h2 className="text-[#212121]/80 uppercase text-xs font-bold tracking-wider">ORDER INFO</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <InputField
                  label="Order Type"
                  placeholder="Standard / Express"
                  value={rawFilters.serviceType}
                  onChange={(e) => handleChange("serviceType", e.target.value)}
                  inputClassName="bg-yellow-50"
                />
                <InputField
                  label="Platform"
                  placeholder="Amazon / Noon"
                  value={rawFilters.platform}
                  onChange={(e) => handleChange("platform", e.target.value)}
                  inputClassName="bg-yellow-50"
                />
                <InputField
                  label="Shipping Company"
                  placeholder="SMSA / Aramex"
                  value={rawFilters.shippingCompany}
                  onChange={(e) => handleChange("shippingCompany", e.target.value)}
                  inputClassName="bg-yellow-50"
                />
                <InputField
                  label="Shipping AWB"
                  placeholder="AWB-XXXXXX"
                  value={rawFilters.shippingAwbNumber}
                  onChange={(e) => handleChange("shippingAwbNumber", e.target.value)}
                  inputClassName="bg-yellow-50"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

